/* eslint-disable max-lines-per-function */
import React from "react";
import { track } from "jiffy-analytics";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import AwardIcon from "assets/icons/bogo-award.svg";
import StickyNoteIcon from "assets/icons/bogo-sticky-note.svg";
import BrushIcon from "assets/icons/brush.svg";

export default function BogoInfo({
  reason = null,
  isGuest = false,
  maxFreeDimensionLabel = '12"x12"',
  ...restProps
}) {
  function trackSignupClick() {
    track("click_discount_signup_link", {
      current_page: "DTF001"
    });
  }

  function headerTitle() {
    // Cohort C (Blanks-Only, no transfers): reason = 'dtf' (first-time DTF buyer)
    if (reason === "dtf") {
      return "You trust our Blanks. Now complete the look, on us.";
    }
    // Cohort A (Guest/Net New) and Cohort B (Transfers-Only): reason = 'both' or 'blanks'
    return "Get your blanks from us, and get your transfers FREE.";
  }

  function blanksImageUrl() {
    return remoteImageStaticUrl("gen/bogo-transfers/blanks-g300.jpg", {
      w: 190,
      fit: "clip",
      q: 80
    });
  }

  function transfersImageUrl() {
    return remoteImageStaticUrl("gen/bogo-transfers/colored-sticker-full.png", {
      w: 324,
      fit: "clip",
      q: 80
    });
  }

  return (
    <div
      {...restProps}
      className="mb-3.5 overflow-hidden rounded bg-gray-100"
      data-testid="bogo-info"
    >
      {}
      <div className="bg-[#3D65F5] px-2 py-3.5 lg:px-3.5" data-testid="bogo-info-header">
        <h3
          className="font-inter m-0 text-xs font-bold leading-[1.15] text-white lg:text-base"
          data-testid="bogo-info-header-title"
        >
          {headerTitle()}
        </h3>
      </div>
      {}
      <div
        className="rounded-b border-[1.5px] bg-gray-100 p-2 lg:p-3"
        data-testid="bogo-info-content"
      >
        <div className="rounded bg-white p-2 pt-3.5 lg:p-1">
          {}
          <div
            className="flex flex-col items-center gap-1.5 lg:flex-row-reverse lg:items-start lg:gap-2 xl:gap-4"
            data-testid="bogo-info-product-section"
          >
            {}
            <div
              className="relative flex flex-shrink-0 items-center justify-center gap-7 lg:gap-1.5"
              data-testid="bogo-info-cards"
            >
              {}
              <figure
                className="m-0! flex! relative h-[126px] w-[104px] flex-col items-center rounded border-[1.5px] border-gray-100 bg-white pt-0.5 text-center lg:h-[180px] lg:w-[147px] lg:border-[3px] lg:pt-1 xl:h-[193px] xl:w-[158px] xl:pt-0.5"
                data-testid="bogo-info-blanks-card"
              >
                <img
                  className="h-[96px] w-auto object-contain lg:h-[132px] xl:h-[143px]"
                  src={blanksImageUrl()}
                  alt="Blank t-shirt"
                  loading="lazy"
                  data-testid="bogo-info-blanks-image"
                />

                <figcaption
                  className="absolute bottom-0.5 left-0 right-0 text-[9px] font-bold leading-[2] text-gray-700 lg:bottom-1 lg:text-[13px] xl:text-sm"
                  data-testid="bogo-info-blanks-caption"
                >
                  {" "}
                  Buy Blanks{" "}
                </figcaption>
              </figure>
              {}
              <div
                className="z-10 -mx-[23px] flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-xl font-bold text-white lg:h-[38px] lg:w-[38px] lg:text-2xl xl:h-[42px] xl:w-[42px] xl:text-[28px]"
                data-testid="bogo-info-plus-sign"
              >
                {" "}
                +{" "}
              </div>
              {}
              <figure
                className="m-0! flex! relative h-[126px] w-[104px] flex-col items-center rounded border-[1.5px] border-white bg-[#B5DEFF]/50 pt-1.5 text-center lg:h-[180px] lg:w-[147px] lg:border-[3px] lg:pt-2 xl:h-[193px] xl:w-[158px] xl:pt-2.5"
                data-testid="bogo-info-transfers-card"
              >
                <img
                  className="h-[92px] w-auto object-contain lg:h-[128px] xl:h-[135px]"
                  src={transfersImageUrl()}
                  alt="Transfer design mockup"
                  loading="lazy"
                  data-testid="bogo-info-transfers-image"
                />

                <figcaption
                  className="absolute bottom-0.5 left-0 right-0 text-[9px] font-bold leading-[2] text-gray-700 lg:bottom-1 lg:text-[13px] xl:text-sm"
                  data-testid="bogo-info-transfers-caption"
                >
                  {" "}
                  Get FREE Transfers{" "}
                </figcaption>
              </figure>
            </div>
            {}
            <div
              className="flex flex-1 flex-col justify-start text-center lg:pl-2 lg:pt-2.5 lg:text-left xl:pl-3 xl:pt-2"
              data-testid="bogo-info-description"
            >
              {}
              <h4
                className="m-0 mb-1 text-base font-bold leading-6 text-gray-700 lg:text-sm lg:leading-relaxed xl:mb-0 xl:text-lg"
                data-testid="bogo-info-how-it-works-title"
              >
                {" "}
                How it works{" "}
              </h4>
              <p
                className="mb-2.5! lg:mb-7! xl:mb-6! px-6 text-[13px] leading-[1.4] text-gray-700 lg:px-0 lg:text-sm lg:leading-[1.86] xl:text-base xl:leading-relaxed"
                data-testid="bogo-info-how-it-works-text"
              >
                {" "}
                For every blank you add, get a{" "}
                {maxFreeDimensionLabel === "any size" ? (
                  <> standard transfer of any size </>
                ) : (
                  <> standard transfer {maxFreeDimensionLabel} or smaller </>
                )}
                <strong>FREE!</strong>
              </p>
              <p
                className="text-lg font-bold text-gray-700 lg:text-xs xl:text-lg"
                data-testid="bogo-info-guarantee"
              >
                {" "}
                100% Satisfaction Guaranteed{" "}
              </p>
            </div>
          </div>
          {}
          <div
            className="mb-2 h-px bg-gray-200 lg:mb-2.5 lg:mt-2 xl:mx-1.5"
            data-testid="bogo-info-divider"
          />

          {}
          <ul
            className="gap-x-6.5 lg:gap-y-6.5 m-0 flex list-none flex-wrap justify-center gap-y-2 p-0"
            data-testid="bogo-info-benefits"
          >
            <li className="flex items-center gap-2" data-testid="bogo-info-benefit-weeding">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]">
                <AwardIcon className="h-[11px] w-[11px] text-white" data-testid="h-[11px]" />
              </span>
              <span className="text-xs leading-relaxed text-gray-600">No Weeding/Layering</span>
            </li>
            <li className="flex items-center gap-2" data-testid="bogo-info-benefit-heat-press">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]">
                <StickyNoteIcon className="h-[11px] w-[11px] text-white" data-testid="h-[11px]-1" />
              </span>
              <span className="text-xs leading-relaxed text-gray-600">Same Heat Press</span>
            </li>
            <li className="flex items-center gap-2" data-testid="bogo-info-benefit-artist-edits">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]">
                <BrushIcon className="h-[11px] w-[11px] text-white" data-testid="h-[11px]-2" />
              </span>
              <span className="text-xs leading-relaxed text-gray-600">Free Artist Edits</span>
            </li>
          </ul>
          {}
          <p
            className="mb-0! xl:mt-1.5! mt-2! lg:mb-1! text-center text-xs leading-relaxed text-gray-600"
            data-testid="bogo-info-disclaimer"
          >
            {isGuest ? (
              <>
                {" "}
                *Offer valid for logged-in customers on their first purchase in each Jiffy business
                line per account. Limit one (1) redemption per household.{" "}
                <a
                  className="underline"
                  href="/signup"
                  data-testid="bogo-info-signup-link"
                  onClick={trackSignupClick}
                >
                  {" "}
                  Create an account{" "}
                </a>{" "}
                to get the discount at checkout.{" "}
              </>
            ) : (
              <>
                {" "}
                *Offer valid for first-time purchases within each Jiffy business line per customer
                account. Limit one (1) redemption per household. Discount applied at checkout.{" "}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
